import React, { useEffect, useState } from 'react';
import {
  formatNavReconAutoApproveWindow,
  formatNavReconDuration,
  getNavReconTimerState,
} from '../navReconTimerUtils';
import { formatUtcTimestampLocalTime } from '../../../dashboard/utils/date.utils';

const TICK_MS = 30_000;

interface Props {
  receivedTimestamp: string | null;
}

/**
 * Live countdown for the 2-hour window after NAV Reconciliation Calculation
 * completes (same duration as Tom's audit auto-approve timer).
 * Approved funds / incomplete calculation pass null (no timer).
 */
const NavReconNavReceivedTimerCell: React.FC<Props> = ({
  receivedTimestamp,
}) => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!receivedTimestamp) return undefined;

    const id = window.setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [receivedTimestamp]);

  const timer = getNavReconTimerState(receivedTimestamp, nowMs);

  if (!timer) {
    return (
      <span className="text-sm text-gray-400" aria-label="No NAV timer">
        —
      </span>
    );
  }

  const receivedLabel =
    formatUtcTimestampLocalTime(receivedTimestamp, {
      second: undefined,
    }) ?? 'unknown';

  if (timer.isOverdue) {
    const overdueMs = Math.abs(timer.remainingMs);
    return (
      <span
        className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-800"
        title={`Calculation completed ${receivedLabel}. Past ${formatNavReconAutoApproveWindow()} window.`}
      >
        +{formatNavReconDuration(overdueMs)}
      </span>
    );
  }

  const remainingRatio =
    timer.remainingMs / (timer.elapsedMs + timer.remainingMs || 1);
  const isUrgent = remainingRatio <= 0.25;

  return (
    <span
      className={`rounded-md border px-2 py-0.5 text-xs font-semibold ${
        isUrgent
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-emerald-200 bg-emerald-50 text-emerald-900'
      }`}
      title={`Calculation completed ${receivedLabel}. Auto-approve window: ${formatNavReconAutoApproveWindow()}.`}
    >
      {formatNavReconDuration(timer.remainingMs)}
    </span>
  );
};

export default NavReconNavReceivedTimerCell;
